import Axios, { isAxiosError } from 'axios';
import {
    BillApiApiBillGetCorportationInfoGet,
    type BillApiApiBillGetCorportationInfoGetRequest,
} from '@clients/BusApi';
import {
    EntityApiApiEntityAddUserEntityPost,
    EntityApiApiEntityConnectEntityToBillPost,
    EntityApiApiEntityGetUserEntitiesGet,
    EntityApiApiEntityUpdateUserEntityPut,
    type EntityApiApiEntityUpdateUserEntityPutRequest,
    type UserEntityDto,
    type UserEntityRequest,
} from '@clients/AccountingApi';
import {
    BillPaymentApiApiBillPaymentGatewaysPost,
    type GatewayDto,
} from '@clients/PaymentApi';
import {
    GiftCardPaymentApiApiGiftCardPaymentGetBalanceGet,
    GiftCardApiApiGiftCardUserGiftCardsGet,
    type UserGiftCardsResponse,
} from '@clients/GiftcardApi';
import {
    BillsApiApiBillsBillCodeAssignUserPut,
    CouponsApiApiBillsBillCodeCouponsDelete,
    UserScoresApiApiBillsBillCodeApplicableUserScoreGet,
    UserScoresApiApiBillsBillCodeApplyUserScorePost,
    UserScoresApiApiUserScoresRecievedGet,
} from '@clients/BillApi';
import {
    type ApplyCouponCodeRequestDto,
    CouponApiApiCouponApplyPost,
    CouponApiApiCouponPublicPartnerPartnerIdGet,
    Providers,
} from '@clients/CouponApi';
import type BillHandler from '@itinerary/BillHandler';
import HotelBillHandler from '@itinerary/hotel/HotelBillHandler';
import type ItineraryPackage from '@itinerary/ItineraryPackage';
import BusBillHandler from '@itinerary/trip/bus/BusBillHandler';
import FlightBillHandler from '@itinerary/trip/flight/FlightBillHandler';
import TrainBillHandler from '@itinerary/trip/train/TrainBillHandler';
import {
    CouponStatus,
    type DiscountEntry,
    discountTitles,
    DiscountType,
    GiftCardStatus,
    type FinalizationStatus,
    InformationRegisterStatus,
    type InvoiceEntry,
    type InvoiceSessionData,
    type ItineraryPackageInvoiceEntries,
} from '@store/invoice';
import ItineraryModule from '@itinerary/ItineraryModule';
import { type PaxType } from '@utils/types';
import { ReserveType } from '@utils/itinerary';
import { type SubmitBuyerPassenger } from '@store/form';
import { PaxTypeTitles } from '@itinerary/trip/TripModuleTypes';
import FlightAutoBillHandler from '@itinerary/trip/flight/FlightAutoBillHandler';
import {
    type ReserveTransferInfoDto,
    type RoomReserve,
} from '@clients/HotelApi';
import { type CaptchaInput } from '@clients/TrainApi';

export class InvoiceStore {
    //#region state
    /**
     * Handler for the bill, that is used to prepare bill for finalization
     *
     * @type {BillHandler[]}
     * @memberof InvoiceStore
     */
    billHandlers: BillHandler[] = [];

    /**
     * Price entries of the bill
     *
     * @type {ItineraryPackageInvoiceEntries[]}
     * @memberof InvoiceStore
     */
    allEntries: ItineraryPackageInvoiceEntries[] = [];

    /**
     * Price entries of the pre bill
     *
     * @type {ItineraryPackageInvoiceEntries[]}
     * @memberof InvoiceStore
     */
    preEntries: ItineraryPackageInvoiceEntries[] = [];

    isLoadingPreEntries = false;

    isLoadingEntries = false;

    /**
     * List of discount entries
     *
     * @type {DiscountEntry[]}
     * @memberof InvoiceStore
     */
    discounts: DiscountEntry[] = [];

    /**
     * Code of the bill
     *
     * @type {string | null}
     * @memberof InvoiceStore
     */
    billCode: string | null = null;

    mac: string | null = null;

    /**
     * Code of discount coupon
     *
     * @type {string | null}
     * @memberof InvoiceStore
     */
    couponCode: string | null = null;

    /**
     * Text of the message of the discount coupon
     *
     * @type {string}
     * @memberof InvoiceStore
     */
    couponMessage = '';

    /**
     * The status of discount coupon
     *
     * @type {CouponStatus}
     * @memberof InvoiceStore
     */
    couponStatus: CouponStatus = CouponStatus.None;

    /**
     * ID of the bill
     *
     * @type {number | null}
     * @memberof InvoiceStore
     */
    billId: number | null = null;

    reserveId: number | null = null;

    /**
     * Status of information registeration
     *
     * @type {InformationRegisterStatus}
     * @memberof InvoiceStore
     */
    informationRegisterStatus: InformationRegisterStatus =
        InformationRegisterStatus.None;

    registerInfoResult: any = null;

    /**
     * Information of passengers of the bill
     *
     * @type {Passenger[]}
     * @memberof InvoiceStore
     */
    pax: SubmitBuyerPassenger[] = [];

    /**
     * Email of the user which bill belongs to
     *
     * @type {string | null}
     * @memberof InvoiceStore
     */
    email: string | null = null;

    /**
     * Mobile number of the user which bill belongs to
     *
     * @type {string | null}
     * @memberof InvoiceStore
     */
    mobile: string | null = null;

    /**
     * Phone number of the user
     *
     * @type {string | null}
     * @memberof InvoiceStore
     */
    landline: string | null = null;

    /**
     * Second mobile number of the user which bill belongs to
     *
     * @type {string | null}
     * @memberof InvoiceStore
     */
    destinationMobile: string | null = null;

    /**
     * Customized data of the bill
     */
    customData: Record<any, any> | null = null;

    isLoadFromSession = false;

    /**
     * Indicates whether to use credit for payment or not
     *
     * @type {boolean}
     * @memberof InvoiceStore
     */
    useCredit = false;

    gateway = '';
    gateWays: GatewayDto[] = [];
    mainGateways: GatewayDto[] = [];
    paymentLoading = false;

    /**
     * Indicates whether to show the price change message or not
     *
     * @type {boolean}
     * @memberof InvoiceStore
     */
    priceChangeMessageShown = false;

    /**
     * Applicable score on the bill
     *
     * @type {(number | null)}
     * @memberof InvoiceStore
     */
    applicableScore: number | null = null;

    /**
     * The amount of money that will be discounted in exchange of user scores
     *
     * @type {(number | null)}
     * @memberof InvoiceStore
     */
    applicableScoreAmount: number | null = null;

    /**
     * Current total score of the user
     *
     * @type {(number | null)}
     * @memberof InvoiceStore
     */
    totalScore: number | null = null;

    editingItem = '';

    userEntities: UserEntityDto[] = [];

    /**
     * received club score from current trip
     *
     * @memberof InvoiceStore
     */
    receivedScore = 0;
    lastReceivedKey = '';

    giftCardStatus: GiftCardStatus = GiftCardStatus.None;
    // gift card codes will store and send to api in upperCase letters
    giftCardCode: string | null = null;
    giftCardMessage = '';
    giftCardAmount: number | null = null;
    userGiftCards: UserGiftCardsResponse[] = [];
    giftCardLoading = false;

    //#endregion

    /**
     * Sets the price entries of the bill
     *
     * @memberof InvoiceStore
     * @param entries
     */
    setEntries(entries: ItineraryPackageInvoiceEntries[]) {
        this.allEntries = entries.map(e => ({
            itineraryPackage: e.itineraryPackage,
            prices: e.prices.filter(item => item.count > 0),
        }));
    }

    /**
     * GETTER - Returns all the price entries of the bill
     *
     * @memberof InvoiceStore
     */
    get entries(): ItineraryPackageInvoiceEntries[] {
        return this.allEntries.map(entry => {
            const ent: ItineraryPackageInvoiceEntries = {
                itineraryPackage: entry.itineraryPackage,
                prices: entry.prices.map(entry => ({
                    paxType: entry.paxType,
                    title: entry.title || PaxTypeTitles[entry.paxType],
                    price: entry.price,
                    count: entry.count,
                })),
            };
            const optionalEntry = this.optionalServiceEntries.find(
                e => e.itineraryPackage === entry.itineraryPackage
            );
            if (optionalEntry) {
                ent.prices.push(...optionalEntry.prices);
            }
            return ent;
        });
    }

    get payFullyWithCredit() {
        const authStore = useAuthStore();
        const userCredit = authStore.credit || 0;
        return this.useCredit && userCredit >= this.totalPriceUnformatted;
    }

    get payFullyWithGiftCard() {
        return (
            this.giftCardStatus === GiftCardStatus.Accepted &&
            this.totalPriceUnformatted === 0 &&
            !this.payFullyWithCredit
        );
    }

    /**
     * clear pre entries and final entries
     *
     * @memberof InvoiceStore
     */
    clearAllEntries() {
        this.setEntries([]);
        this.preEntries = [];
    }

    /**
     * change count of entry price of entry index by paxTypes
     *
     * @param {{
     *      entryIndex: number;
     *      paxType: PaxType;
     *      count: number;
     *  }} data
     * @memberof InvoiceStore
     */
    changeEntryPriceCountByPaxType(data: {
        entryIndex: number;
        paxType: PaxType;
        count: number;
    }) {
        const priceIndex = this.allEntries[data.entryIndex].prices.findIndex(
            p => p.paxType === data.paxType
        );
        this.allEntries[data.entryIndex].prices[priceIndex].count = data.count;
    }

    /**
     * Apply a discount to bill's price
     *
     * @param {{ type: DiscountType; price: number }} params
     * @memberof InvoiceStore
     */
    addDiscount(params: { type: DiscountType; price: number }) {
        if (params.price <= 0) {
            return;
        }
        this.discounts.push({
            type: params.type,
            price: params.price,
            title: discountTitles[params.type],
        });
    }

    /**
     * GETTER - Returns discount entries of the bill
     *
     * @memberof InvoiceStore
     */
    get titledDiscounts() {
        const discounts = this.discounts.map(entry => ({
            type: entry.type,
            title: discountTitles[entry.type],
            price: entry.price,
        }));
        return this.giftCardAmount
            ? [
                  ...discounts,
                  {
                      title: 'اعتبار سفر کارت',
                      price: this.giftCardAmount,
                      type: DiscountType.Special,
                  },
              ]
            : discounts;
    }

    /**
     * Removes a discount from bill's price
     *
     * @param {DiscountType} type
     * @memberof InvoiceStore
     */
    removeDiscount(type: DiscountType) {
        const start = this.discounts.findIndex(d => d.type === type);
        if (start !== -1) {
            this.discounts.splice(start, 1);
        }
    }

    /**
     * Sets the bill's information (code and id)
     *
     * @param {{ code: string; id: number; reserveId: number | null }} params
     * @memberof InvoiceStore
     */
    setBillInfo(params: {
        code: string | null;
        id: number | null;
        reserveId: number | null;
        mac?: string | null;
    }) {
        this.billCode = params.code;
        this.billId = params.id;
        this.reserveId = params.reserveId;
        this.mac = params?.mac || null;
    }

    /**
     * Sets coupon's information (message, code and status)
     *
     * @param {{ message: string; code: string; status: CouponStatus }} params
     * @memberof InvoiceStore
     */
    setCoupon(params: { message: string; code: string; status: CouponStatus }) {
        this.couponCode = params.code;
        this.couponMessage = params.message;
        this.couponStatus = params.status;
    }

    /**
     * Sets the contact information of the user which bill belongs to
     *
     * @param {{email: string;mobile: string;destinationMobile: string;landline: string}} params
     * @memberof InvoiceStore
     */
    setContact(params: {
        email: string;
        mobile: string;
        landline: string;
        destinationMobile: string;
    }) {
        this.email = params.email;
        this.mobile = params.mobile;
        this.landline = params.landline;
        this.destinationMobile = params.destinationMobile;
    }

    /**
     * Sets the information of passengers of the bill
     *
     * @memberof InvoiceStore
     * @param pax
     */
    setTitledPax(pax: SubmitBuyerPassenger[]) {
        this.pax = pax.map(p => ({
            ...p,
            title: this.billHandlers[0]?.getTitle(p.type!, p.gender!) || '',
        }));
    }

    /**
     * Clears all information of state of bill module
     *
     * @memberof InvoiceStore
     */
    clearState() {
        this.setEntries([]);
        this.preEntries = [];
        this.discounts = [];
        this.giftCardAmount = null;
        this.giftCardStatus = GiftCardStatus.None;
        this.giftCardCode = null;
        this.giftCardMessage = '';
        this.billCode = null;
        this.mac = null;
        this.couponCode = null;
        this.couponMessage = '';
        this.couponStatus = CouponStatus.None;
        this.billId = null;
        this.informationRegisterStatus = InformationRegisterStatus.None;
    }

    /**
     * Sets applicable score by the user for discount
     *
     * @memberof InvoiceStore
     */
    setApplicableScores(payload: {
        amount: number;
        score: number;
        totalScore: number;
    }) {
        this.applicableScore = payload.score;
        this.applicableScoreAmount = payload.amount;
        this.totalScore = payload.totalScore;
    }

    /**
     * GETTER - Returns the total price of the bill(considering discounts)
     *
     * @memberof InvoiceStore
     */
    get totalPrice() {
        return this.totalPriceUnformatted;
    }

    get showNonCashPayment() {
        return useFormStore().itineraryPackages.some(pkg => pkg.canUseDiscount);
    }

    /**
     * GETTER - Returns the total price of the bill, as a number
     *
     * @memberof InvoiceStore
     */
    get totalPriceUnformatted() {
        return (
            this.entries.reduce(
                (acc, curr) =>
                    acc +
                    curr.prices.reduce(
                        (pacc, pcurr) => pacc + pcurr.price * pcurr.count,
                        0
                    ),
                0
            ) -
            this.discounts.reduce((acc, curr) => acc + curr.price, 0) -
            (this.giftCardAmount || 0)
        );
    }

    get preInvoiceTotalPrice(): number {
        return this.preEntries.reduce(
            (acc, curr) =>
                acc +
                curr.prices.reduce(
                    (pacc, pcurr) => pacc + pcurr.price * pcurr.count,
                    0
                ),
            0
        );
    }

    get preInvoiceTotalDiscount(): number {
        return this.preEntries.reduce(
            (acc, curr) =>
                acc +
                curr.prices
                    .filter(p => !!p.discount)
                    .reduce(
                        (pacc, pcurr) => pacc + pcurr.discount! * pcurr.count,
                        0
                    ),
            0
        );
    }

    /**
     * GETTER - Returns the total amount of discount of pre-bill
     *
     * @memberof PackageSelectStore
     */
    get preInvoiceDiscounts() {
        const discountType = this.preEntries.every(f =>
            f.prices.every(p => p.discountType === DiscountType.FirstPurchase)
        )
            ? DiscountType.FirstPurchase
            : DiscountType.Special;
        return <DiscountEntry[]>[
            {
                type: discountType,
                title: discountTitles[discountType],
                price: this.preInvoiceTotalDiscount,
            },
        ].filter(d => d.price > 0);
    }

    get preInvoiceEntries(): ItineraryPackageInvoiceEntries[] {
        return this.preEntries.map(p => ({
            ...p,
            prices: p.prices.map(pr => ({
                ...pr,
                price: pr.price + pr.discount!,
                title: pr.title || PaxTypeTitles[pr.paxType],
            })),
        }));
    }

    /**
     * GETTER - Returns the total price of the bill without considering discounts
     *
     * @memberof InvoiceStore
     */
    get totalPriceWithoutDiscount() {
        return this.totalPriceWithoutDiscountUnformatted;
    }

    /**
     * GETTER - Returns the total price of the bill, before the discounts being applied
     *
     * @memberof InvoiceStore
     */
    get totalPriceWithoutDiscountUnformatted() {
        return this.entries.reduce(
            (acc, curr) =>
                acc +
                curr.prices.reduce(
                    (pacc, pcurr) => pacc + pcurr.price * pcurr.count,
                    0
                ),
            0
        );
    }

    get optionalServiceEntries(): ItineraryPackageInvoiceEntries[] {
        const formStore = useFormStore();
        const groupedServices: ItineraryPackageInvoiceEntries[] = [];
        formStore.paxForms.forEach(pax => {
            pax.optionalServiceList.forEach(s => {
                if (s.selectedService && s.selectedService.id !== 'none') {
                    const service = groupedServices.find(
                        e => e.itineraryPackage === s.itineraryPackage
                    );
                    if (service) {
                        const serviceItem = service.prices.find(
                            p => p.title === s.selectedService!.label
                        );
                        if (serviceItem) {
                            serviceItem.count++;
                        } else {
                            service.prices.push({
                                title: s.selectedService.label,
                                count: 1,
                                paxType: pax.type,
                                price: s.selectedService.price,
                            });
                        }
                    } else {
                        const entry: ItineraryPackageInvoiceEntries = {
                            itineraryPackage: s.itineraryPackage!,
                            prices: [],
                        };
                        entry.prices.push({
                            title: s.selectedService.label,
                            count: 1,
                            paxType: pax.type,
                            price: s.selectedService.price,
                        });
                        groupedServices.push(entry);
                    }
                }
            });
            pax.freeServiceList.forEach(s => {
                s.selectedServices?.forEach(selectedService => {
                    if (selectedService) {
                        const service = groupedServices.find(
                            e => e.itineraryPackage === s.itineraryPackage
                        );
                        if (service) {
                            const serviceItem = service.prices.find(
                                p => p.title === selectedService!.label
                            );
                            if (serviceItem) {
                                serviceItem.count++;
                            } else {
                                service.prices.push({
                                    title: selectedService.label,
                                    count: 1,
                                    paxType: pax.type,
                                    price: selectedService.price,
                                });
                            }
                        } else {
                            const entry: ItineraryPackageInvoiceEntries = {
                                itineraryPackage: s.itineraryPackage!,
                                prices: [],
                            };
                            entry.prices.push({
                                title: selectedService.label,
                                count: 1,
                                paxType: pax.type,
                                price: selectedService.price,
                            });
                            groupedServices.push(entry);
                        }
                    }
                });
            });
        });
        return groupedServices.map(gs => ({
            itineraryPackage: gs.itineraryPackage,
            prices: gs.prices.filter(Boolean).map(p => ({
                ...p,
                totalPrice: p.price * p.count,
            })),
        }));
    }

    /**
     * ACTION - Applies discount coupon to bill
     *
     * @param {{ code: string }} params
     * @memberof InvoiceStore
     */
    async applyCoupon(params: { code: string }) {
        if (params.code === 'newmrbilit-DF07') {
            params.code = 'unique';
        }
        this.couponStatus = CouponStatus.Checking;
        try {
            const applyCouponCodeRequestDto: ApplyCouponCodeRequestDto = {
                couponCode: params.code,
                billCode: this.billCode,
            };
            const smId = useSafarMarket().getSmId();
            if (smId) {
                applyCouponCodeRequestDto.source = Providers.NUMBER_3;
                applyCouponCodeRequestDto.sourceToken = smId;
            }
            const { data } = await CouponApiApiCouponApplyPost({
                applyCouponCodeRequestDto,
            });
            if (data.isApplied) {
                this.setCoupon({
                    message: data.message || '',
                    code: params.code,
                    status: CouponStatus.Accepted,
                });
                this.removeDiscount(DiscountType.FirstPurchase);
                this.addDiscount({
                    type: DiscountType.Coupon,
                    price: data.discountValue || 0,
                });
            } else {
                this.removeDiscount(DiscountType.FirstPurchase);
                this.setCoupon({
                    message: data.message || '',
                    code: '',
                    status: CouponStatus.Rejected,
                });
            }
        } catch (ex) {
            reportException(ex);
            if (isAxiosError(ex)) {
                this.setCoupon({
                    message: ex.response?.data.message,
                    code: '',
                    status: CouponStatus.Rejected,
                });
            }
        }
    }

    setGiftCard(params: {
        amount?: number;
        code: string | null;
        status: GiftCardStatus;
        message?: string | null;
        remainedBalance?: number | null;
    }) {
        this.giftCardStatus = params.status;
        this.giftCardAmount = params.amount || null;
        this.giftCardCode = params.code?.toUpperCase() || null;
        this.giftCardMessage = params.message || '';
    }

    removeGiftCard() {
        this.setGiftCard({ code: null, status: GiftCardStatus.None });
    }

    async terminateReserveActions() {
        if (this.reserveId) {
            await useCurrentItineraryModule().value.terminatePackageReserve(
                this.reserveId
            );
        }
    }

    async applyGiftCard(giftCardCode: string) {
        this.setGiftCard({
            code: null,
            status: GiftCardStatus.Checking,
        });

        try {
            const { data } =
                await GiftCardPaymentApiApiGiftCardPaymentGetBalanceGet({
                    giftCardCode: giftCardCode.toUpperCase(),
                    amount: this.totalPriceUnformatted,
                    billCode: this.billCode || undefined,
                });
            this.setGiftCard({
                amount: data.appliedAmount,
                code: giftCardCode,
                message: data.massage,
                status: GiftCardStatus.Accepted,
            });
        } catch (e: any) {
            reportException(e);
            this.setGiftCard({
                code: null,
                message: e.response?.data?.message,
                status: GiftCardStatus.Rejected,
            });
        }
    }

    async loadGiftCards() {
        this.giftCardLoading = true;
        try {
            const { data } = await GiftCardApiApiGiftCardUserGiftCardsGet();
            this.userGiftCards = data.map(giftCard => ({
                ...giftCard,
                giftCardCode: giftCard.giftCardCode?.toUpperCase(),
            }));
        } catch (e) {
            reportException(e);
        } finally {
            this.giftCardLoading = false;
        }
    }

    /**
     * ACTION - Cancels using discount coupon
     *
     * @memberof InvoiceStore
     */
    cancelCoupon() {
        this.removeDiscount(DiscountType.Coupon);
        this.setCoupon({
            message: '',
            code: '',
            status: CouponStatus.None,
        });
    }

    initializeGateway() {
        if (sessionStorage.getItem('rayanCard') === 'true') {
            this.gateway = 'RayanCard';
        } else if (
            sessionStorage.getItem('tara') === 'true' ||
            window.posthog.persistence?.props.utm_source === 'tara'
        ) {
            this.gateway = 'tara';
        } else {
            this.gateway = '';
        }
    }

    saveSession(persistent = false) {
        const storage = persistent ? localStorage : sessionStorage;
        storage.setItem(
            'invoiceData',
            JSON.stringify({
                pax: this.pax, // pure
                contact: {
                    email: this.email || '',
                    mobile: this.mobile || '',
                    landline: this.landline || '',
                    destinationMobile: this.destinationMobile || '',
                }, // pure
                customData: this.customData, // pure
                billInfo: {
                    code: this.billCode,
                    id: this.billId,
                    reserveId: this.reserveId,
                    mac: this.mac,
                },
                gateway: this.gateway, // pure
                useCredit: this.useCredit, // pure
                discounts: this.discounts, // pure
                entries: this.entries, // impure
                billHandlers: this.billHandlers, // impure
            })
        );
    }

    loadSession() {
        const dataStr =
            sessionStorage.getItem('invoiceData') ||
            localStorage.getItem('invoiceData');
        if (
            !dataStr ||
            [
                InformationRegisterStatus.Registered,
                InformationRegisterStatus.RegisteredFromSession,
            ].includes(this.informationRegisterStatus)
        ) {
            return;
        }
        const data = JSON.parse(dataStr) as InvoiceSessionData;
        this.setTitledPax(data.pax);
        this.setContact(data.contact);
        this.customData = data.customData;
        this.useCredit = data.useCredit;
        this.discounts = data.discounts;
        this.setEntries(
            data.entries.map(entry => ({
                ...entry,
                itineraryPackage: entry.itineraryPackage
                    ? ItineraryModule.revivePackage(entry.itineraryPackage)!
                    : undefined,
            }))
        );
        this.billHandlers = data.billHandlers.map(handler => {
            let concrete: BillHandler | undefined;
            switch (handler.concreteType) {
                case ReserveType.Train:
                    concrete = new TrainBillHandler();
                    break;
                case ReserveType.Hotel:
                    concrete = new HotelBillHandler();
                    break;
                case ReserveType.Bus:
                    concrete = new BusBillHandler(0, 0, false);
                    break;
                case ReserveType.Flight:
                    if (handler.isAutoReserve) {
                        concrete = new FlightAutoBillHandler();
                    } else {
                        concrete = new FlightBillHandler('', []);
                    }
                    break;
            }
            Object.assign(concrete!, handler);
            return concrete!;
        });
        this.isLoadFromSession = true;
        this.informationRegisterStatus =
            InformationRegisterStatus.RegisteredFromSession;
        this.setBillInfo(data.billInfo);
    }

    /**
     * ACTION - Registers the information of passengers of bill
     *
     * @async
     * @param {{pax: Passenger[];email: string;mobile: string;destinationMobile: string;customData: any;}} params
     * @memberof InvoiceStore
     */
    async registerPaxInfo(params: {
        pax: SubmitBuyerPassenger[];
        email: string;
        mobile: string;
        landline: string;
        destinationMobile: string;
        formDirty?: boolean;
        customData: {
            summary: BusSummary[];
            rooms: RoomReserve[] | undefined;
            requestFromHotel?: string;
            isFloatingReserve: boolean;
            reserveTransferInfo?: ReserveTransferInfoDto;
            captcha?: CaptchaInput;
        };
        message?: string;
        floating?: boolean;
    }) {
        const autoReserveStore = useAutoReserveStore();
        const { isAutoReserve } = autoReserveStore;
        this.informationRegisterStatus = InformationRegisterStatus.Registering;
        if (isAutoReserve) {
            await autoReserveStore.createReserve();
        }
        if (this.billId === null || this.billCode === null) {
            return;
        }
        try {
            if (this.billHandlers === null) {
                return;
            }
            const results: boolean[] = [];
            for (const index in this.billHandlers) {
                const handler = this.billHandlers[index];
                const result = await handler[
                    isAutoReserve
                        ? 'autoReserveRegisterPaxInfo'
                        : 'registerPaxInfo'
                ]({
                    ...params,
                    billId: this.billId,
                    billCode: this.billCode,
                    entries: this.entries[0]?.prices || [],
                    bypassPaxCountValidation: params.formDirty,
                });
                this.registerInfoResult = result.data || null;
                results.push(result.result);
            }

            this.setTitledPax(params.pax);
            this.setContact({
                email: params.email,
                mobile: params.mobile,
                landline: params.landline,
                destinationMobile: params.destinationMobile,
            });
            this.customData = params.customData;
            this.saveSession(params.floating);
            return results.every(Boolean);
        } catch (ex) {
            this.informationRegisterStatus = InformationRegisterStatus.Error;
            throw ex;
        }
    }

    /**
     * ACTION - Checks the bill before payment whether it had prepay and whether it is allowed for payment or not
     *
     * @async
     * @memberof InvoiceStore
     */
    async checkBillBeforePayment() {
        if (this.billCode === null) {
            return;
        }

        if (this.billHandlers === null) {
            return;
        }
        const unifiedHandlers = this.billHandlers.reduce((all, curr) => {
            if (all.some(b => b.concreteType === curr.concreteType)) {
                return all;
            } else {
                return [...all, curr];
            }
        }, [] as BillHandler[]);
        await Promise.all(
            unifiedHandlers.map(h =>
                h.prepayReservation(this.billCode!, {
                    price: this.totalPriceUnformatted,
                })
            )
        );
    }

    /**
     * ACTION - Proceeds the user to gateway for bill payment
     *
     * @memberof InvoiceStore
     */
    async proceedToGateway(captcha?: string) {
        const handled = await useAutoReserveStore().proceedPayment();
        if (!handled) {
            const gateway = this.gateway ? `&gateway=${this.gateway}` : '';
            const captchaQuery = captcha ? `&captcha=${captcha}` : '';
            const giftCardCode = this.giftCardCode
                ? `&giftCardCode=${this.giftCardCode}`
                : '';
            window.location.href = `${useBaseUrls().payment}/api/billpayment/${
                this.billCode
            }?payFromCredit=${this.useCredit}&access_token=${
                useAuthStore().token
            }${gateway}${giftCardCode}${captchaQuery}`;
        }
    }

    async assignBillToLoggedInUser(xPlayerID?: string | null) {
        if (!this.billCode || !xPlayerID) {
            return;
        }
        try {
            await BillsApiApiBillsBillCodeAssignUserPut({
                billCode: this.billCode,
                xPlayerID,
            });
        } catch (e) {
            reportException(e);
        }
    }

    async loadApplicableScore() {
        if (
            !this.billCode ||
            !useAuthStore().loggedIn ||
            this.applicableScore !== null
        ) {
            return;
        }
        const { data } =
            await UserScoresApiApiBillsBillCodeApplicableUserScoreGet({
                billCode: this.billCode,
            });
        this.setApplicableScores({
            amount: data.ApplicableAmount || 0,
            score: data.ApplicableScore || 0,
            totalScore: data.Score || 0,
        });
    }

    async applyUserScore() {
        if (!this.billCode || !useAuthStore().loggedIn) {
            return;
        }
        await UserScoresApiApiBillsBillCodeApplyUserScorePost({
            billCode: this.billCode,
        });
        this.addDiscount({
            type: DiscountType.Score,
            price: this.applicableScoreAmount || 0,
        });
    }

    async cancelUserScore() {
        await CouponsApiApiBillsBillCodeCouponsDelete({
            billCode: this.billCode || '',
        });
        this.removeDiscount(DiscountType.Score);
    }

    async getTicketFile(data: {
        billCode: string;
        mac: string;
    }): Promise<FinalizationStatus> {
        Axios.defaults.headers.common.Authorization = '';
        return (
            await Axios.get(
                `${useBaseUrls().finalize}/workflow/bill/${
                    data.billCode
                }/status?mac=${data.mac}`
            )
        ).data;
    }

    async getPartnerCoupon(partnerId: number) {
        try {
            const { data } = await CouponApiApiCouponPublicPartnerPartnerIdGet({
                partnerId,
            });
            return data;
        } catch (e) {
            reportException(e);
        }
    }

    setPreEntryOfPackage(data: {
        itineraryPackage: ItineraryPackage;
        entries: InvoiceEntry[];
    }) {
        const entry = this.preEntries.find(
            e => e.itineraryPackage === data.itineraryPackage
        );
        if (entry) {
            entry.prices = data.entries;
        } else {
            this.preEntries.push({
                itineraryPackage: data.itineraryPackage,
                prices: data.entries,
            });
        }
    }

    /**
     * ACTION - Loads fares of the currently selected price class, and provides information for the prebill to be rendered
     *
     * @async
     * @memberof PackageSelectStore
     */
    async loadPreInvoice(itineraryPackage: ItineraryPackage) {
        const { getPassCount } = useSearchFormStore();
        if (!itineraryPackage?.selectedPrices?.length) {
            this.setPreEntryOfPackage({
                itineraryPackage,
                entries: [],
            });
            return;
        }
        this.isLoadingPreEntries = true;
        const entries = await itineraryPackage.loadPreInvoice({
            ...getPassCount(itineraryPackage),
            loggedIn: useAuthStore().loggedIn,
        });
        this.isLoadingPreEntries = false;

        this.setPreEntryOfPackage({
            itineraryPackage,
            entries,
        });
    }

    async loadUserEntities() {
        try {
            const { data } = await EntityApiApiEntityGetUserEntitiesGet();
            this.userEntities = data;
        } catch (e) {
            reportException(e);
        }
    }

    async addUserEntity(request: UserEntityRequest) {
        try {
            await EntityApiApiEntityAddUserEntityPost({
                userEntityRequest: request,
            });
            return true;
        } catch (e) {
            reportException(e);
            return false;
        }
    }

    async updateUserEntity(
        request: EntityApiApiEntityUpdateUserEntityPutRequest
    ) {
        try {
            await EntityApiApiEntityUpdateUserEntityPut(request);
            return true;
        } catch (e) {
            reportException(e);
            return false;
        }
    }

    async connectEntityToBill(entityId: number) {
        try {
            await EntityApiApiEntityConnectEntityToBillPost({
                billId: this.billId || undefined,
                entityId,
            });
        } catch (e) {
            reportException(e);
        }
    }

    async loadCorporationInfo(
        params: BillApiApiBillGetCorportationInfoGetRequest
    ) {
        try {
            // void type returned
            const { data }: { data: unknown } =
                await BillApiApiBillGetCorportationInfoGet(params);
            return data as { phone: string };
        } catch (e) {
            reportException(e);
        }
    }

    async loadReceivedTripScore() {
        const formStore = useFormStore();
        const authStore = useAuthStore();
        if (this.billCode && authStore.loggedIn) {
            const receivedKey = `${this.billCode}-${formStore.adultCount}-${formStore.childCount}-${formStore.infantCount}`;
            if (this.lastReceivedKey === receivedKey) {
                return;
            } else {
                this.lastReceivedKey = receivedKey;
            }
            try {
                const { data } = await UserScoresApiApiUserScoresRecievedGet({
                    billCode: this.billCode,
                    adultCount: formStore.adultCount,
                    childCount: formStore.childCount,
                    infantCount: formStore.infantCount,
                });
                this.receivedScore = data.Score || 0;
            } catch (e) {
                reportException(e);
                this.receivedScore = 0;
                this.lastReceivedKey = '';
            }
        } else {
            this.receivedScore = 0;
            this.lastReceivedKey = '';
        }
    }

    //#region gateways
    async getGateways() {
        const { data } = await BillPaymentApiApiBillPaymentGatewaysPost();
        this.gateWays = data?.gateways || [];
        this.mainGateways =
            data?.gateways?.filter(({ isMain }) => isMain) || [];
        this.gateway = this.gateWays[0]?.gatewayRedirectParameter || '';
    }
    //#endregion
}

export const { use: useInvoiceStore } = defineClassStore(
    'invoice',
    () => new InvoiceStore()
);
